import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import useMe from '@/hooks/useMe';
import axiosClient from '@/utils/axiosClient';
import Section from '../Section';

const formSchema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
});

const AccountSettings = () => {
  const currentUser = useMe();
  const selectedAccount = currentUser?.data?.accounts.find(
    (account) => account.id === currentUser.data?.selected_account_id,
  );

  const queryClient = useQueryClient();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
    },
  });

  useEffect(() => {
    if (selectedAccount && selectedAccount.name) {
      const formValues = {
        name: selectedAccount.name || '',
      };
      form.reset(formValues as any);
    }
  }, [selectedAccount]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { handleSubmit } = form;

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setIsSubmitting(true);

    try {
      const response = await axiosClient.put(`v1/accounts/${selectedAccount?.id}`, {
        name: data.name,
      });
      if (response.status === 200) {
        queryClient.clear();
        window.location.reload();
      } else {
        setIsSubmitting(false);
      }
    } catch (error) {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <div>
        <div className="flex flex-col justify-between gap-4 mb-8 md:flex-row sm:gap-8">
          <div className="flex flex-col">
            <div className="text-3xl font-bold leading-6 text-neutral-800 sm:text-2xl">Account Settings</div>
            <div className="mt-2 font-normal leading-normal text-gray-500 text-md md:text-sm">
              Change your account settings here.
            </div>
          </div>
        </div>
      </div>
      <Section title="Account Information">
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="w-full space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <div className="w-1/4">
                  <FormItem>
                    <FormLabel>Account Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter the name of your new account" autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </div>
              )}
            />
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Update Account'}
            </Button>
          </form>
        </Form>
      </Section>
    </>
  );
};

export default AccountSettings;
